import { Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { MSS_FORMLY_IMPORTS } from '@shared/components/formly/formly-standalone-imports';
import { LoadingComponent } from '@shared/components/loading/loading.component';
import { appName } from '@shared/constants';
import { AppType, UserFeedbackCategory, UserFeedbackType } from '@shared/models/enums/user-feedback-enums';
import { IUserFeedback } from '@shared/models/user-feedback';
import { ModalService, ToastService } from '@shared/services';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';

@Component({
  selector: 'lib-app-user-feedback',
  templateUrl: './user-feedback.component.html',
  imports: [FormsModule, ReactiveFormsModule, ...MSS_FORMLY_IMPORTS, LoadingComponent],
})
export class UserFeedbackComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly clubHubDataService = inject(ClubHubDataService);

  public form = new UntypedFormGroup({});
  public fields: FormlyFieldConfig[] = [];
  public formOptions: FormlyFormOptions = {};
  public model = {};
  public formVisible = true;
  public feedback: IUserFeedback[] = [];
  public completed = false;

  public feedbackTypes = [
    { value: UserFeedbackType.Improvement, label: 'New feature idea' },
    { value: UserFeedbackType.Bug, label: 'Something is broken' },
  ];

  public feedbackCategories = [
    { value: UserFeedbackCategory.Affordability, label: 'Affordability', appType: AppType.All },
    { value: UserFeedbackCategory.ClaimsForm, label: 'Claims form', appType: AppType.ClubHub },
    { value: UserFeedbackCategory.Criteria, label: 'Criteria', appType: AppType.All },
    { value: UserFeedbackCategory.EOR, label: 'EOR', appType: AppType.ClubHub },
    { value: UserFeedbackCategory.ESIS, label: 'ESIS', appType: AppType.All },
    { value: UserFeedbackCategory.GeneralSite, label: 'General site', appType: AppType.All },
    { value: UserFeedbackCategory.MI, label: 'MI', appType: AppType.All },
    { value: UserFeedbackCategory.MyAccount, label: 'My account', appType: AppType.All },
    { value: UserFeedbackCategory.Product, label: 'Product', appType: AppType.All },
    { value: UserFeedbackCategory.Property, label: 'Property', appType: AppType.ClubHub },
  ];

  public ngOnInit() {
    this.initializeFeedback();
  }

  private initializeFeedback(): void {
    this.fields = [
      {
        key: 'feedbackType',
        type: 'select',
        defaultValue: null,
        props: {
          label: 'Feedback type',
          options: this.feedbackTypes,
          required: true,
        },
      },
      {
        key: 'feedbackCategory',
        type: 'select',
        defaultValue: null,
        props: {
          label: 'Which part of the site is the feedback referring to?',
          // Show different options based on app
          options:
            (window as any).appName === appName.clubHub
              ? this.feedbackCategories.filter(cat => cat.appType === AppType.ClubHub || cat.appType === AppType.All || cat.value === null)
              : this.feedbackCategories.filter(cat => cat.appType === AppType.All || cat.value === null),
          required: true,
        },
      },
      {
        key: 'feedback',
        type: 'textarea',
        props: {
          label: 'Feedback details',
          rows: 4,
          required: true,
        },
      },
    ];
  }

  public onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const { feedbackType, feedbackCategory, feedback } = this.form.value;

      const requestData = {
        typeId: feedbackType,
        categoryId: feedbackCategory,
        feedback: feedback,
      };

      this.modalService.close();

      this.clubHubDataService.post<boolean>('UserFeedback', requestData).subscribe({
        next: () => {
          this.completed = true;
          this.toastService.success('Feedback submitted successfully!');
        },
        error: () => this.showError(),
      });

      this.formOptions.resetModel?.();
      this.formVisible = false;
    }
  }

  public onCancel() {
    this.modalService.dismiss();
  }

  private showError() {
    this.toastService.danger('Feedback was not sent. Please try again.');
  }
}
